import React, { useEffect, useState } from 'react';
import { 
    DocumentCard, 
    DocumentCardDetails, 
    DocumentCardType, 
    Icon, 
    IDocumentCardStyles, 
    ITextStyles, 
    Text, 
    mergeStyleSets,
} from '@fluentui/react';
import BreadcrumbComponent from '../../components/breadcrumb/breadcrumb.component';
import LoaderComponent from '../../components/loader/loader.component';
import { MapContainer, TileLayer } from 'react-leaflet';
import DatePickerComponent from '../../components/date-picker/date-picker.component';
import { LatLng } from 'leaflet';
import ButtonPrimaryComponent from '../../components/button-primary/button-primary.component';
import LeafletMapMarkerComponent from '../../components/map-marker/leaflet-map-marker.component';
import { AzureDataPosition, AzureMap, AzureMapDataSourceProvider, AzureMapsProvider } from 'react-azure-maps';
import { AuthenticationType } from 'azure-maps-control';
import AzureMapMarkerComponent from '../../components/map-marker/azure-map-marker.component';
import GoogleMapsMapComponent from '../../components/google-maps/google-maps-map.component';
import GoogleMapMarkerComponent from '../../components/map-marker/google-map-marker.component';
import ErrorMessageComponent from '../../components/error-message/error-message.component';
import ModalDefaultComponent from '../../components/modal-default/modal-default.component';
import WorkOrderComponent from '../../components/work-order/work-order.component';
import WorkEditComponent from '../../components/work/work-edit.component';
import NoPermissionComponent from '../../components/no-permission/no-permission.component';

import ApiService from '../../services/api/api.service';
import useAuthStatus from '../../hooks/auth/AuthHook';
import { useNavigate } from 'react-router-dom';

import ItemRegistroTiempo from '../../models/ItemRegistroTiempo';
import ItemOrdenDeTrabajo from '../../models/ItemOrdenDeTrabajo';
import ItemTarea from '../../models/ItemTarea';
import ElementEnum from '../../enums/ElementEnum';
import ItemPermiso from '../../models/ItemPermiso';
import moment from 'moment';

const getLeafletLatLng = (value: string) : LatLng => {
    const valueArray : string[] = value.split(',');
    return new LatLng(parseFloat(valueArray[0]), parseFloat(valueArray[1]));
};

const getAzureLatLng = (value: string) : AzureDataPosition => {
    const valueArray : string[] = value.split(',');
    return [parseFloat(valueArray[1]), parseFloat(valueArray[0])];
};

const getGoogleLatLng = (value: string) : google.maps.LatLngLiteral => {
    if (value.includes(',')) {
        const valueArray : string[] = value.split(',');
        return {
            lat: parseFloat(valueArray[0]),
            lng: parseFloat(valueArray[1]),
        };
    }
    else {
        const valueArray : string[] = value.split(';');
        return {
            lat: parseFloat(valueArray[0]),
            lng: parseFloat(valueArray[1]),
        };
    }
};

const onParseDateFromString = (newValue: string) : Date => {
    if (newValue) {
        return moment(newValue, "YYYY-MM-DD").toDate();
    }
    else {
        return new Date();
    }
};

const onFormatDate = (date: Date) : string => {
    let pFecha : string = 
        (date.getFullYear()) + '-' +
        ((date.getMonth() + 1) < 10 ? ('0' + (date.getMonth() + 1)) : (date.getMonth() + 1)) + "-" +
        (date.getDate() < 10 ? ('0' + date.getDate()) : date.getDate());
        
    return pFecha;
};

const cardStyle: IDocumentCardStyles = {
    root: {
        backgroundColor: "#ffffff",
        minWidth: "calc(100% - 20px)",
        padding: 0,
        color: "rgb(50, 49, 48)",
        height: 'auto',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        marginBottom: 8,
        overflow: 'hidden',
    },
};

const selectedCardStyle: IDocumentCardStyles = {
    root: {
        backgroundColor: "#ffffff",
        minWidth: "calc(100% - 20px)",
        padding: 0,
        borderColor: "var(--color-primario)",
        borderWidth: 2,
        color: "rgb(50, 49, 48)",
        height: 'auto',
        borderRadius: 12,
        boxShadow: '0 4px 16px rgba(237,109,99,0.25)',
        marginBottom: 8,
        overflow: 'hidden',
        selectors: {
            ":hover": {
                borderColor: "var(--color-primario)",
            },
        },
    },
};

const textStyles: ITextStyles = {
    root: {
        color: 'inherit',
    },
};

const chronoClasses = mergeStyleSets({
    cardInner: {
        display: 'flex',
        alignItems: 'stretch',
    },
    sidebarPlay: {
        width: 6,
        flexShrink: 0,
        backgroundColor: '#4CAF50',
        borderRadius: '12px 0 0 12px',
    },
    sidebarPause: {
        width: 6,
        flexShrink: 0,
        backgroundColor: '#FF9800',
        borderRadius: '12px 0 0 12px',
    },
    cardContent: {
        padding: '10px 12px',
        flex: 1,
    },
    cardMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 4,
    },
    cardActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 6,
        marginTop: 8,
    },
    iconPlay: {
        color: '#4CAF50',
        fontSize: 12,
    },
    iconPause: {
        color: '#FF9800',
        fontSize: 12,
    },
    metaRight: {
        color: '#999',
        marginLeft: 'auto',
    },
    mapContainer: {
        width: '100%',
        height: '100%',
        minHeight: 400,
    },
});

interface IChronologyPageProps {};

const ChronologyPage = (props: IChronologyPageProps) => {
    const navigate = useNavigate();
    const auth = useAuthStatus();

    const mapType: string = "azure";

    const workOrdersPermission : ItemPermiso = auth.getPermission(ElementEnum.WORK_ORDERS);

    const [loading, setLoading] = useState<boolean>(false);
    const [fromDate, setFromDate] = useState<Date>(new Date());
    const [toDate, setToDate] = useState<Date>(new Date());
    const [workParts, setWorkParts] = useState<ItemRegistroTiempo[]>([]);
    const [selectedWorkPart, setSelectedWorkPart] = useState<ItemRegistroTiempo>();
    const [selectedStart, setSelectedStart] = useState<boolean>(false);
    const [workOrder, setWorkOrder] = useState<ItemOrdenDeTrabajo>();
    const [workOrderOpen, setWorkOrderOpen] = useState<boolean>(false);
    const [work, setWork] = useState<ItemTarea>();
    const [workOpen, setWorkOpen] = useState<boolean>(false);

    useEffect(() => {
        if (auth.user) {
            if (auth.user.ModoBienvenida) {
                navigate('/main/bienvenida/');
            }
            else if (workOrdersPermission.Ver && fromDate && toDate) {
                setLoading(true);
                ApiService.Post<ItemRegistroTiempo[]>('OrdenesTrabajo', 'GetTareaTiempos', auth.user, {
                    "IdOrden": 0,
                    "IdTarea": 0,
                    "FechaDesde": fromDate,
                    "FechaHasta": toDate,
                    "Usuario": "",
                }).then((response: ItemRegistroTiempo[]) => {
                    setWorkParts(response);
                    setLoading(false);
                }, (error: string) => {
                    alert(error);
                    setLoading(false);
                });
            }
        }
    }, [auth.user, fromDate, toDate, workOrdersPermission.Ver, navigate]);

    useEffect(() => {
        if (workParts.length) {
            setSelectedWorkPart(workParts[0]);
            setSelectedStart(true);
        }
        else {
            setSelectedWorkPart(undefined);
            setSelectedStart(false);
        }
    }, [workParts]);

    const onFromDateChange = (value: string) : void => {
        setFromDate(onParseDateFromString(value));
    };

    const onToDateChange = (value: string) : void => {
        setToDate(onParseDateFromString(value));
    };

    const onRowClick = (item: ItemRegistroTiempo, start: boolean) : void => {
        setSelectedWorkPart(item);
        setSelectedStart(start);
    };

    const onSelectMapMarker = (item: ItemRegistroTiempo, start: boolean) : void => {
        setSelectedWorkPart(item);
        setSelectedStart(start);
    };

    const onSelectWorkOrder = (item: ItemRegistroTiempo) : void => {
        if (auth.user) {
            setLoading(true);
            ApiService.Post<ItemOrdenDeTrabajo>("OrdenesTrabajo", "Get", auth.user, {
                "IdOrden": item.IdOrden,
            }).then((response: ItemOrdenDeTrabajo) => {
                setWorkOrder(response);
                setWorkOrderOpen(true);
                setLoading(false);
            }, (error: string) => {
                alert(error);
                setLoading(false);
            });
        }
    };

    const onSelectWork = (item: ItemRegistroTiempo) : void => {
        if (auth.user) {
            setLoading(true);
            ApiService.Post<ItemOrdenDeTrabajo>("OrdenesTrabajo", "Get", auth.user, {
                "IdOrden": item.IdOrden,
            }).then((response: ItemOrdenDeTrabajo) => {
                let wo : ItemOrdenDeTrabajo = response;
                setWork(wo.ListaTareas.find((work: ItemTarea) => {
                    return (work.Id === item.IdTarea);
                }));
                setWorkOrder(wo);
                setWorkOpen(true);
                setLoading(false);
            }, (error: string) => {
                alert(error);
                setLoading(false);
            });
        }
    };

    return (
        workOrdersPermission.Ver ? (
            <div className='d-flex flex-column h-100 w-100'>
                <div className="row m-0">
                    <div className='col-12'>
                        <BreadcrumbComponent items={["Inicio", "Cronología"]} />
                    </div>
                </div>
                <div className="row m-0 flex-fill overflow-hidden">
                    <div className="col-12 col-md-6 h-50 h-md-100 px-2 overflow-auto">
                        <div className="row m-0">
                            <div className='col-12 col-md-6'>
                                <DatePickerComponent 
                                    value={onFormatDate(fromDate)}
                                    onChange={onFromDateChange} />
                            </div>
                            <div className='col-12 col-md-6'>
                                <DatePickerComponent 
                                    value={onFormatDate(toDate)}
                                    onChange={onToDateChange} />
                            </div>
                            <div className="col-12 py-3">
                                {
                                    (workParts.length === 0) ? (
                                        <ErrorMessageComponent message='No hay datos para mostrar' />
                                    ) : (
                                        workParts.map((workPart: ItemRegistroTiempo) => 
                                            <div key={workPart.Id}>
                                                {
                                                    workPart.PosicionGPSInicio ? (
                                                        <DocumentCard 
                                                            styles={(workPart.Id === selectedWorkPart?.Id && selectedStart) ? selectedCardStyle : cardStyle}
                                                            type={DocumentCardType.compact}
                                                            onClick={() => onRowClick(workPart, true)}>
                                                            <DocumentCardDetails>
                                                                <div className={chronoClasses.cardInner}>
                                                                    <div className={chronoClasses.sidebarPlay} />
                                                                    <div className={chronoClasses.cardContent}>
                                                                        <div className={chronoClasses.cardMeta}>
                                                                            <Icon iconName='Play' className={chronoClasses.iconPlay} />
                                                                            <Text variant="smallPlus" styles={textStyles}>
                                                                                <strong>{workPart.IdUsuario}</strong>
                                                                                {' · OT: '}{workPart.IdOrden}
                                                                            </Text>
                                                                            <Text variant="xSmall" styles={{ root: { color: '#999', marginLeft: 'auto' } }}>
                                                                                {workPart.Inicio}
                                                                            </Text>
                                                                        </div>
                                                                        <Text variant="small" styles={{ root: { color: '#666', display: 'block', marginBottom: 8 } }}>
                                                                            {workPart.Descripcion}
                                                                        </Text>
                                                                        <div className={chronoClasses.cardActions}>
                                                                            <ButtonPrimaryComponent
                                                                                text='OT'
                                                                                secondary={true}
                                                                                onClick={() => onSelectWorkOrder(workPart)} />
                                                                            <ButtonPrimaryComponent
                                                                                text='Trabajo'
                                                                                secondary={true}
                                                                                onClick={() => onSelectWork(workPart)} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </DocumentCardDetails>
                                                        </DocumentCard>
                                                    ) : (
                                                        null
                                                    )
                                                }
                                                {
                                                    workPart.PosicionGPSFinal ? (
                                                        <DocumentCard 
                                                            styles={(workPart.Id === selectedWorkPart?.Id && !selectedStart) ? selectedCardStyle : cardStyle}
                                                            type={DocumentCardType.compact}
                                                            onClick={() => onRowClick(workPart, false)}>
                                                            <DocumentCardDetails>
                                                                <div className={chronoClasses.cardInner}>
                                                                    <div className={chronoClasses.sidebarPause} />
                                                                    <div className={chronoClasses.cardContent}>
                                                                        <div className={chronoClasses.cardMeta}>
                                                                            <Icon iconName='Pause' className={chronoClasses.iconPause} />
                                                                            <Text variant="smallPlus" styles={textStyles}>
                                                                                <strong>{workPart.IdUsuario}</strong>
                                                                                {' · OT: '}{workPart.IdOrden}
                                                                            </Text>
                                                                            <Text variant="xSmall" styles={{ root: { color: '#999', marginLeft: 'auto' } }}>
                                                                                {workPart.Fin}
                                                                            </Text>
                                                                        </div>
                                                                        <Text variant="small" styles={{ root: { color: '#666', display: 'block', marginBottom: 8 } }}>
                                                                            {workPart.Descripcion}
                                                                        </Text>
                                                                        <div className={chronoClasses.cardActions}>
                                                                            <ButtonPrimaryComponent
                                                                                text='OT'
                                                                                secondary={true}
                                                                                onClick={() => onSelectWorkOrder(workPart)} />
                                                                            <ButtonPrimaryComponent
                                                                                text='Trabajo'
                                                                                secondary={true}
                                                                                onClick={() => onSelectWork(workPart)} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </DocumentCardDetails>
                                                        </DocumentCard>
                                                    ) : (
                                                        null
                                                    )
                                                }
                                            </div>
                                        )
                                    )
                                }
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-md-6 h-50 h-md-100 overflow-hidden px-2">
                        {
                            workParts.length ? (
                                (mapType === "leaflet") ? (
                                    <MapContainer 
                                        center={getLeafletLatLng(workParts[0].PosicionGPSInicio)} 
                                        zoom={13}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            minHeight: 400,
                                        }}>
                                        <TileLayer
                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                        {
                                            workParts.map((wp: ItemRegistroTiempo) => (
                                                <LeafletMapMarkerComponent
                                                    key={wp.Id}
                                                    workPart={wp}
                                                    selectedWorkPart={selectedWorkPart}
                                                    selectedStart={selectedStart}
                                                    onSelect={onSelectMapMarker} />
                                            ))
                                        }
                                    </MapContainer>
                                ) : (mapType === "azure") ? (
                                    <AzureMapsProvider>
                                        <div className={chronoClasses.mapContainer}>
                                            <AzureMap 
                                                options={{
                                                    authOptions: {
                                                        authType: AuthenticationType.subscriptionKey,
                                                        subscriptionKey: '3Qqlzj_T3oymm37vAdIMIs2H4jUbPZXxtbkHg03LONw',
                                                    },
                                                    center: getAzureLatLng(workParts[0].PosicionGPSInicio),
                                                    zoom: 13,
                                                }}>
                                                <AzureMapDataSourceProvider 
                                                    id={'AzureMapDataSourceProvider'}
                                                    options={{
                                                        lineMetrics: true,
                                                    }}>
                                                    {
                                                        workParts.map((wp: ItemRegistroTiempo) => (
                                                            <AzureMapMarkerComponent
                                                                key={wp.Id}
                                                                workPart={wp}
                                                                selectedWorkPart={selectedWorkPart}
                                                                selectedStart={selectedStart}
                                                                onSelect={onSelectMapMarker} />
                                                        ))
                                                    }
                                                </AzureMapDataSourceProvider>
                                            </AzureMap>
                                        </div>
                                    </AzureMapsProvider>
                                ) : (mapType === "google") ? (
                                    <GoogleMapsMapComponent
                                        zoom={13}
                                        center={getGoogleLatLng(workParts[0].PosicionGPSInicio)}>
                                        {
                                            workParts.map((wp: ItemRegistroTiempo) => (
                                                <GoogleMapMarkerComponent
                                                    key={wp.Id}
                                                    workPart={wp}
                                                    selectedWorkPart={selectedWorkPart}
                                                    selectedStart={selectedStart}
                                                    onSelect={onSelectMapMarker} />
                                            ))
                                        }
                                    </GoogleMapsMapComponent>
                                ) : (
                                    null
                                )
                            ) : (
                                null
                            )
                        }
                    </div>
                </div>
                {
                    (workOrderOpen && workOrder) ? (
                        <ModalDefaultComponent
                            title="Orden de trabajo"
                            onDismiss={() => {
                                setWorkOrder(undefined);
                                setWorkOrderOpen(false);
                            }}>
                            <WorkOrderComponent 
                                workOrder={workOrder}
                                setLoading={setLoading} />
                        </ModalDefaultComponent>
                    ) : (
                        null
                    )
                }
                {
                    (workOpen && workOrder && work) ? (
                        <WorkEditComponent
                            workOrder={workOrder}
                            work={work}
                            onClose={() => {
                                setWork(undefined);
                                setWorkOrder(undefined);
                                setWorkOpen(false);
                            }}
                            onWorkEdited={() => {}} />
                    ) : (
                        null
                    )
                }
                <LoaderComponent loading={loading} />
            </div>
        ) : (
            <NoPermissionComponent showButton={true} />
        )
    );
};

export default ChronologyPage;
